/**
 * Friend leagues, over the database functions in supabase/schema.sql. Every
 * call runs as the signed-in learner; the server checks membership and only
 * ever returns nicknames, weekly XP and streaks.
 */

import { getSupabase } from '../lib/supabaseClient.js';
import { on, EVENTS } from '../app/eventBus.js';
import { getUserProgress } from '../repositories/progressRepository.js';
import { getSession } from './authService.js';
import { getSyncStatus, syncNow } from './syncService.js';
import { describeStanding, leagueWeek, normalizeInviteCode } from './leagueCore.js';

/** Supabase errors carry the server's own message ("That league is full."), which is already learner-friendly. */
function friendly(error) {
  if (error instanceof TypeError || error?.message === 'Failed to fetch') {
    return new Error("Couldn't reach the server. Check your connection and try again.");
  }
  return new Error(error?.message || 'Something went wrong. Try again.');
}

async function call(fn, args = {}) {
  const client = await getSupabase();
  if (!client) throw new Error('Accounts are not set up for this deployment.');
  const { data, error } = await client.rpc(fn, args);
  if (error) throw friendly(error);
  return data;
}

export const getNickname = () => call('my_display_name');
export const setNickname = (name) => call('set_display_name', { p_name: name }).then(forgetSummary);
export const listLeagues = async () => (await call('my_leagues')) || [];

export async function createLeague(name) {
  const rows = await call('create_league', { p_name: name });
  forgetSummary();
  return rows?.[0] || null;
}

export async function joinLeague(code) {
  const clean = normalizeInviteCode(code);
  if (!clean) throw new Error('Invite codes are 8 letters and numbers, like QM7K-4RTX.');
  const rows = await call('join_league', { p_code: clean });
  forgetSummary();
  return rows?.[0] || null;
}

export const leaveLeague = (leagueId) => call('leave_league', { p_league: leagueId }).then(forgetSummary);

/** Standings for one league. Pushes this device's latest XP first so your own row is current. */
export async function getStandings(leagueId, { pushFirst = true } = {}) {
  if (pushFirst) await syncNow().catch(() => {});
  return (await call('league_standings', { p_league: leagueId })) || [];
}

/* ------------------------- dashboard summary ------------------------- */

const SUMMARY_MAX_AGE_MS = 5 * 60 * 1000;
let summary = null; // { at, value }

function forgetSummary(value) {
  summary = null;
  return value;
}
on(EVENTS.authChanged, () => forgetSummary());

/** The last loaded summary, if it is recent enough to show at once. */
export function peekLeagueSummary() {
  return summary && Date.now() - summary.at < SUMMARY_MAX_AGE_MS ? summary.value : null;
}

/**
 * The learner's position in their first league, for the dashboard:
 * `{ leagues, league?, rank?, size?, weeklyXp?, message?, daysLeft }`, or null when
 * signed out. Pushes this device's progress first only if it has changed since the
 * last sync, so a just-finished session counts without writing on every visit.
 */
export async function loadLeagueSummary() {
  const session = await getSession();
  if (!session) return forgetSummary(null);

  const leagues = await listLeagues();
  const { daysLeft } = leagueWeek();
  let value = { leagues: leagues.length, daysLeft };
  if (leagues.length) {
    const { lastSyncedAt } = getSyncStatus();
    const changed = !lastSyncedAt || new Date(getUserProgress().updatedAt) > new Date(lastSyncedAt);
    const rows = await getStandings(leagues[0].id, { pushFirst: changed });
    value = { ...value, league: leagues[0], ...describeStanding(rows) };
  }
  summary = { at: Date.now(), value };
  return value;
}
